import { GetServerSidePropsContext } from 'next';
import { trackingData as globalTrackingData } from '../../functions/app/global.meta';
import {
  findAllSportSeasonById,
  IAllSportSeason
} from '../../functions/server/rewriter-data/allSeasonIds';
import { getUserDataFromMemberCookie } from '../../functions/utils/userUtils';
import { getTeamLevel } from './../../constants/teamLevel';
import { fromEntries } from './../../functions/primitiveHelpers/objectHelpers';

const MOBILE_SITE_TYPE = 'mobile web';
const MOBILE_BRAND_PLATFORM_ID = 'maxpreps_site_mweb';
const DESKTOP_SITE_TYPE = 'desktop web';
const DESKTOP_BRAND_PLATFORM_ID = 'maxpreps_site_desktop';

const PROPERTY_MAP = new Map();
PROPERTY_MAP.set('gender', 'gender');
PROPERTY_MAP.set('sport', 'sportName');
PROPERTY_MAP.set('sportName', 'sportName');
PROPERTY_MAP.set('level', 'teamLevel');
PROPERTY_MAP.set('season', 'season');
PROPERTY_MAP.set('year', 'year');
PROPERTY_MAP.set('schoolId', 'schoolId');
PROPERTY_MAP.set('teamId', 'schoolId');
PROPERTY_MAP.set('schoolFormattedName', 'schoolName');
PROPERTY_MAP.set('formattedName', 'schoolName');
PROPERTY_MAP.set('stateCode', 'state');
PROPERTY_MAP.set('state', 'state');
PROPERTY_MAP.set('careerId', 'careerId');
PROPERTY_MAP.set('careerName', 'careerName');

const normalizeContextualData = (
  data: Record<string, string>
): Map<string, string> => {
  const formattedData = new Map();

  if (data) {
    for (const [api, tracking] of PROPERTY_MAP.entries()) {
      if (typeof data[api] !== 'undefined') {
        formattedData.set(tracking, data[api]);
      }
    }
  }

  return formattedData;
};

// need to get the brandplatformid from the query too which is where the apps put it
const getDeviceData = deviceType => {
  if (deviceType === 'mobile') {
    return {
      brandPlatformId: MOBILE_BRAND_PLATFORM_ID,
      siteType: MOBILE_SITE_TYPE
    };
  }

  return {
    brandPlatformId: DESKTOP_BRAND_PLATFORM_ID,
    siteType: DESKTOP_SITE_TYPE
  };
};

const getLocationContextData = () => {
  if (typeof window === 'undefined') return {};

  return {
    pageUrl: window.location.href
  };
};

const MEMBER_AGE_RANGE = [18, 25, 35, 45, 55, 64, 9999];
const getMemberAge = (birthdate: string | null) => {
  if (!!birthdate) {
    return;
  }

  try {
    const age =
      new Date().getFullYear() - parseInt(birthdate?.split('/').pop() ?? '35'); // picked 35 arbitrarily - technically this should never happen
    let index = -1;

    for (let i = 0; i < MEMBER_AGE_RANGE.length; i++) {
      if (age <= MEMBER_AGE_RANGE[i]) {
        break;
      }

      index = i;

      // Too young, dont pass anything
      if (index === -1) {
        return;
      }

      if (index === MEMBER_AGE_RANGE.length - 1) {
        return '64+';
      }

      return `${MEMBER_AGE_RANGE[index]}-${MEMBER_AGE_RANGE[index + 1]}`;
    }
  } catch (error) {
    console.error(error);
  }
};

const getTrackingMemberData = () => {
  if (typeof window === 'undefined') return {};

  const userCookie = getUserDataFromMemberCookie();
  const isMember = !!userCookie.id;

  return {
    userId: userCookie.id ?? '',
    userType: isMember ? 'registered' : 'anon',
    userState: isMember ? 'authenticated' : 'not authenticated',

    memberGender: userCookie.gender ?? '',
    memberRole: userCookie.role ?? '',
    memberAge: getMemberAge(userCookie.birthdate) ?? ''
  };
};

const getCalculatedData = (projectMetaData, pageMetaData, isAdmin) => {
  const calculatedData: {
    siteHier?: string[];
    siteSection?: string;
    pageType?: string;
    pageName?: string;
    isAvia?: boolean;
  } = {
    pageType: pageMetaData?.pageType,
    // we are using the pageName data point to track templates
    pageName: pageMetaData?.tracking,
    isAvia: pageMetaData.hasVideoPlayer || projectMetaData.hasVideoPlayer
  };

  if (pageMetaData?.hierarchy || projectMetaData?.hierarchy) {
    calculatedData.siteHier = [
      // because we already if'd this, if pageMeta isn't good we know project is and we can assume it's safe to array'ize
      ...(pageMetaData?.hierarchy || [projectMetaData.hierarchy])
    ];

    if (isAdmin) {
      calculatedData.siteHier.push('utility');
    }

    calculatedData.siteSection = calculatedData?.siteHier[0];
  }

  return calculatedData;
};

export const getUTagObject = (
  existingUtagData,
  projectMetaData,
  pageMetaData,
  contextDataPoints,
  device,
  pageViewGuid,
  isAdmin,
  statusCode = 200
) => {
  const pageError = statusCode >= 400 ? { pageError: statusCode } : null;

  return {
    ...existingUtagData, // Current data
    ...globalTrackingData, // Global data
    ...getTrackingMemberData(), // Member data from cookie
    ...getLocationContextData(), // Context data from window
    ...getDeviceData(device), // Properties derived from device type
    ...getCalculatedData(projectMetaData, pageMetaData, isAdmin), // Page-specific data
    ...fromEntries(normalizeContextualData(contextDataPoints)),
    ...{ pageViewGuid }, // set updated viewguid - needs to share guid with ad system,
    ...pageError
  };
};

/**
 * Get data points that need to be passed directly to Tealium
 * @param nextContext
 * @returns { tracking: { ...data }}
 */
export const getTrackingDataPointsFromRequest = (
  nextContext: GetServerSidePropsContext
): Record<string, string | null> => {
  var query = new URLSearchParams(nextContext.query as any);

  const allSeasonId = query.get('allSeasonId');
  let ass: IAllSportSeason | undefined = undefined;
  if (allSeasonId) {
    ass = findAllSportSeasonById(allSeasonId);
  }

  const ssid = query.get('ssid');
  const season = query.get('season');
  const teamLevel = query.get('teamLevel');
  const state = query.get('state');

  return {
    ssid, // always going to be right
    state: state?.toUpperCase() || null, // always going to be right

    teamLevel: getTeamLevel(teamLevel), // usually going to be right.
    season, // usually going to be right
    // BUG: The unrewriter is returning 'null' for this data point from time to time
    gender: ass?.gender || null, // always going to be right
    sport: ass?.sport || null // always going to be right
    //year: '', currently no good way to extract this
  };
};
